import React, { useCallback, useState } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { Trans } from '@lyearn/i18n';
import {
  Activity2Outline,
  CheckmarkCircle2Outline,
  FileOutline,
  MessageSquare2Outline,
  Trash2Fill,
} from '@lyearn/icons';
import { AlertDialog } from '@lyearn/molecules';
import { Checkbox, Stack, Typography, useSnackbar } from '@lyearn/ui';
import useNavigation from '@/hooks/useNavigation';
import useResponse from '@/hooks/useResponse';
import routes from '@/pages/corporate/routes';

import { useFileTaskCountQuery } from '../../../graphql/queries/generated/fileTaskCount';
import useFileMutations from '../../../hooks/useFileMutations';
import { DialogProps } from '../types';

function DeleteProject({ fileId, onClose, inViewFileId }: DialogProps) {
  const { t } = useTranslation();
  const [isFetching, setIsFetching] = useState(false);
  const [confirmation, setConfirmation] = useState(false);
  const { deleteFile } = useFileMutations();
  const { showSnackbar } = useSnackbar();
  const { handleResponse } = useResponse();
  const navigation = useNavigation();

  const [{ fetching: fetchingTaskCount, data }] = useFileTaskCountQuery({
    variables: {
      filter: {
        id: fileId ?? '',
      },
    },
    pause: !fileId,
  });

  const taskCount = data?.file?.taskCount ?? 0;

  const onDelete = useCallback(async () => {
    if (!fileId) return;
    setIsFetching(true);
    const data = await deleteFile({
      data: {
        fileId,
      },
    });
    setIsFetching(false);

    handleResponse({
      condition: !!data.data?.deleteFile?.success,
      onSuccess: () => {
        showSnackbar({
          message: (
            <Trans
              i18nKey={taskCount ? 'Task:List.ListDeleted' : 'Task:List.ListDeletedWithNoTask'}
              values={{ count: taskCount }}
            />
          ),
          startIcon: <Trash2Fill fillColorToken="icon-placeholder" />,
        });

        onClose();
        if (inViewFileId === fileId) {
          navigation.replace(routes.Projects(), {
            appendQuery: false,
          });
        }
      },
    });
  }, [
    deleteFile,
    fileId,
    handleResponse,
    inViewFileId,
    navigation,
    onClose,
    showSnackbar,
    taskCount,
  ]);

  if (!fileId) {
    return null;
  }

  return (
    <AlertDialog
      header={{
        title: 'Delete Project?',
        description:
          'Deleting this project will permanently remove all its tasks, files, discussions, and activity logs. The following will be permanently deleted:',
      }}
      primaryAction={{
        appearance: 'danger',
        label: t('Common:Actions.Delete'),
        onClick: onDelete,
        disabled: isFetching || fetchingTaskCount || !confirmation,
        loading: isFetching,
      }}
      onClose={onClose}>
      <Stack className="mt-24 px-8" direction="col" gapY={16}>
        <Stack alignItems="center" direction="row" gap={12}>
          <CheckmarkCircle2Outline
            className="w-20 h-20 flex-shrink-0"
            fillColorToken="icon-active"
          />
          <Typography color="text-text-primary" variant="body-short-02">
            {taskCount} {taskCount === 1 ? 'Task' : 'Tasks'}
          </Typography>
        </Stack>
        {/* TODO: @aryan02420 show count of discussions */}
        <Stack alignItems="center" direction="row" gap={12}>
          <MessageSquare2Outline className="w-20 h-20 flex-shrink-0" fillColorToken="icon-active" />
          <Typography color="text-text-primary" variant="body-short-02">
            All Discussions
          </Typography>
        </Stack>
        {/* TODO: @aryan02420 show count of files */}
        <Stack alignItems="center" direction="row" gap={12}>
          <FileOutline className="w-20 h-20 flex-shrink-0" fillColorToken="icon-active" />
          <Typography color="text-text-primary" variant="body-short-02">
            All Files
          </Typography>
        </Stack>
        <Stack alignItems="center" direction="row" gap={12}>
          <Activity2Outline className="w-20 h-20 flex-shrink-0" fillColorToken="icon-active" />
          <Typography color="text-text-primary" variant="body-short-02">
            Activity Feed
          </Typography>
        </Stack>
      </Stack>
      <Stack className="mt-20 px-8" direction="row" gap={8}>
        <Checkbox checked={confirmation} onChange={(e) => setConfirmation(e.target.checked)} />
        <Typography color="text-text-paragraph" variant="label-01">
          I understand this action is irreversible
        </Typography>
      </Stack>
    </AlertDialog>
  );
}

export default DeleteProject;
